import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaDownload, FaEdit, FaMagic } from 'react-icons/fa';
import { fileSave } from 'browser-fs-access';
import { wishesService } from '../services/wishesService';
import TextOverlay from '../components/TextOverlay';
import { renderImageWithText } from '../utils/renderImage';
import { renderVideoWithText } from '../utils/renderVideo';
import { loadGoogleFonts } from '../utils/fonts';
import BottomNav from '../components/public/BottomNav';
import logo from '../assets/logo.svg';
import './AutoModeEditor.css';

const AutoModeEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [mediaData, setMediaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wishText, setWishText] = useState('');
  const [stage, setStage] = useState('form'); // 'form' | 'preview'
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingText, setProcessingText] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [iosSaveModal, setIosSaveModal] = useState({ show: false, blobUrl: '', filename: '' });
  const [textColor, setTextColor] = useState('#FFFFFF');

  const colorOptions = [
    '#FFFFFF', '#FFD700', '#FF6B6B', '#00E676',
    '#40C4FF', '#E040FB', '#FF9100', '#17479E', '#000000'
  ];

  const videoRef = useRef(null);
  const previewContainerRef = useRef(null);
  const inputGroupRef = useRef(null);

  const processingMessages = [
    'Initializing magic...',
    'Adding your text...',
    'Applying beautiful fonts...',
    'Rendering high quality...',
    'Almost there...',
    'Finalizing your masterpiece...'
  ];

  // Build auto text layer
  const autoTextLayer = wishText.trim() ? [{
    id: 1,
    text: wishText,
    fontSize: 28,
    fontFamily: 'Noto Sans',
    color: textColor,
    bold: true,
    italic: false,
    align: 'center',
    x: 50,
    y: 82,
    fadeIn: 0,
    startTime: 0,
    endTime: duration || 999
  }] : [];

  useEffect(() => {
    fetchMediaData();
    loadGoogleFonts().catch(err => console.error('Font load error:', err));
  }, [id]);

  // Scroll down to textarea after 1 second on load
  useEffect(() => {
    if (!loading && stage === 'form' && inputGroupRef.current) {
      const timer = setTimeout(() => {
        inputGroupRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [loading, stage]);

  // Progress simulation
  useEffect(() => {
    let progressInterval;
    let messageInterval;
    let messageIndex = 0;

    if (isProcessing) {
      setProcessingProgress(0);
      setProcessingText(processingMessages[0]);

      progressInterval = setInterval(() => {
        setProcessingProgress((prev) => {
          if (prev >= 95) return prev;
          const increment = prev < 30 ? 8 : prev < 60 ? 5 : prev < 80 ? 3 : 1;
          return Math.min(prev + increment, 95);
        });
      }, 800);

      messageInterval = setInterval(() => {
        messageIndex = (messageIndex + 1) % processingMessages.length;
        setProcessingText(processingMessages[messageIndex]);
      }, 2500);
    } else {
      setProcessingProgress(0);
      setProcessingText('');
    }

    return () => {
      clearInterval(progressInterval);
      clearInterval(messageInterval);
    };
  }, [isProcessing]);

  // Get video duration
  useEffect(() => {
    if (mediaData?.mediaType === 'video' && videoRef.current) {
      const video = videoRef.current;
      const handleLoadedMetadata = () => {
        if (videoRef.current) setDuration(videoRef.current.duration);
      };
      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      if (video.readyState >= 1) setDuration(video.duration);
      return () => video.removeEventListener('loadedmetadata', handleLoadedMetadata);
    }
  }, [mediaData]);

  // Auto-play video when entering preview stage
  useEffect(() => {
    if (stage === 'preview' && mediaData?.mediaType === 'video' && videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, [stage, mediaData]);

  const fetchMediaData = async () => {
    try {
      setLoading(true);
      const response = await wishesService.getMediaById(id);
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const buildUrl = (url) => {
        if (!url) return null;
        if (url.startsWith('http://') || url.startsWith('https://')) return url;
        return `${baseURL}${url}`;
      };
      const data = {
        ...response.data,
        mediaUrl: buildUrl(response.data.mediaUrl),
        thumbnailUrl: buildUrl(response.data.thumbnailUrl)
      };
      setMediaData(data);
    } catch (error) {
      console.error('Error fetching media:', error);
      navigate('/wishes');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'info') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
  };

  const handleCreate = () => {
    if (!wishText.trim()) {
      showToast('Please type your wish first', 'warning');
      return;
    }
    setStage('preview');
  };

  // --- Platform detection helpers (same as MediaEditor) ---
  const isMobileDevice = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isPWAStandalone = () =>
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true ||
    document.referrer.includes('android-app://');

  // --- Download handlers (adapted from MediaEditor) ---
  const handleDownload = async () => {
    try {
      if (autoTextLayer.length === 0) return;
      setIsProcessing(true);
      showToast('Processing your media...', 'info');

      if (mediaData.mediaType === 'image') {
        await downloadImageWithText();
        await wishesService.incrementDownloadCount(id);
      } else {
        await downloadVideoWithText();
        await wishesService.incrementDownloadCount(id);
      }
      setProcessingProgress(100);
      setProcessingText('Complete!');
      await new Promise(resolve => setTimeout(resolve, 500));
      showToast('Saved successfully!', 'success');
    } catch (error) {
      console.error('Error downloading:', error);
      showToast('Error saving media. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadImageWithText = async () => {
    const blob = await renderImageWithText(mediaData.mediaUrl, autoTextLayer);
    await handleImageSave(blob);
  };

  const handleImageSave = async (blob) => {
    const filename = `${mediaData.occasionName.replace(/\s+/g, '_')}_${Date.now()}.png`;
    const inStandaloneMode = isPWAStandalone();

    // Share API first (PWA)
    if (inStandaloneMode && navigator.share && navigator.canShare) {
      try {
        const file = new File([blob], filename, { type: 'image/png' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: mediaData.occasionName, text: `${mediaData.occasionName} Wishes` });
          return;
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
      }
    }

    // iOS
    if (isIOS()) {
      const url = URL.createObjectURL(blob);
      if (inStandaloneMode) {
        const a = document.createElement('a');
        a.href = url; a.download = filename; a.style.display = 'none';
        document.body.appendChild(a); a.click();
        setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
      } else {
        const newWindow = window.open(url, '_blank');
        if (newWindow) setTimeout(() => showToast('Long press image and select "Save Image"', 'info'), 1000);
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      }
      return;
    }

    // Android/Desktop
    try {
      await fileSave(blob, { fileName: filename, extensions: ['.png'], description: 'PNG Image', startIn: 'pictures', id: 'wishes-images' });
    } catch (err) {
      if (err.name === 'AbortError') return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.style.display = 'none';
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
    }
  };

  const downloadVideoWithText = async () => {
    let videoWidth = videoRef.current ? videoRef.current.videoWidth : 1280;
    let videoHeight = videoRef.current ? videoRef.current.videoHeight : 720;
    const previewHeight = previewContainerRef.current ? previewContainerRef.current.offsetHeight : 400;
    const previewWidth = previewContainerRef.current ? previewContainerRef.current.offsetWidth : 600;

    const mobile = isMobileDevice();
    const MAX_MOBILE_HEIGHT = 720;
    if (mobile && videoHeight > MAX_MOBILE_HEIGHT) {
      const scale = MAX_MOBILE_HEIGHT / videoHeight;
      videoWidth = Math.round(videoWidth * scale);
      videoHeight = MAX_MOBILE_HEIGHT;
    }

    const result = await renderVideoWithText(mediaData.mediaUrl, autoTextLayer, {
      width: videoWidth, height: videoHeight,
      previewWidth, previewHeight,
      frameRate: mobile ? 24 : 30,
      quality: mobile ? 'medium' : 'high',
      onProgress: (percent) => setProcessingProgress(percent)
    });

    const { blob, mimeType, isMP4Compatible, conversionFailed } = result;

    if (conversionFailed) showToast('MP4 conversion failed. Video saved as WebM.', 'warning');
    else if (!isMP4Compatible) showToast('Video format may not play on iPhone/WhatsApp.', 'warning');

    const extension = mimeType.includes('mp4') ? 'mp4' : 'webm';
    const filename = `${mediaData.occasionName.replace(/\s+/g, '_')}_wishes.${extension}`;
    const inStandaloneMode = isPWAStandalone();
    const isiOSDevice = isIOS();

    // iOS: Share API first
    if (isiOSDevice && navigator.share && navigator.canShare) {
      try {
        const file = new File([blob], filename, { type: mimeType });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: mediaData.occasionName, text: `${mediaData.occasionName} Wishes` });
          return;
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
      }
    }

    // PWA Standalone (non-iOS)
    if (!isiOSDevice && inStandaloneMode && navigator.share && navigator.canShare) {
      try {
        const file = new File([blob], filename, { type: mimeType });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: mediaData.occasionName, text: `${mediaData.occasionName} Wishes` });
          return;
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
      }
    }

    // iOS fallback
    if (isiOSDevice) {
      const blobUrl = URL.createObjectURL(blob);
      setIosSaveModal({ show: true, blobUrl, filename });
      showToast('Tap and hold the video, then select "Save to Photos"', 'info');
      return;
    }

    // Android/Desktop
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl; a.download = filename; a.style.display = 'none';
    document.body.appendChild(a); a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(blobUrl); }, 100);
  };

  if (loading) {
    return (
      <div className="auto-editor-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!mediaData) {
    return <div>Media not found</div>;
  }

  return (
    <div className="auto-editor">
      {/* Toast */}
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <span>{toast.message}</span>
        </div>
      )}

      {/* Processing Overlay */}
      {isProcessing && (
        <div className="processing-overlay">
          <div className="processing-container">
            <div className="processing-spinner">
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
            </div>
            <div className="processing-info">
              <h3 className="processing-title">Creating Your Wish</h3>
              <p className="processing-message">{processingText}</p>
              <div className="progress-container">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${processingProgress}%` }}>
                    <div className="progress-shimmer"></div>
                  </div>
                </div>
                <div className="progress-percentage">{processingProgress}%</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="auto-editor-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft />
        </button>
        <h2>{mediaData.occasionName}</h2>
        <div className="header-badge">
          <FaMagic /> Quick
        </div>
      </div>

      {/* Media Preview */}
      <div className="auto-editor-preview" ref={previewContainerRef}>
        {mediaData.mediaType === 'image' ? (
          <div className="auto-image-wrapper">
            <img src={mediaData.mediaUrl} alt={mediaData.occasionName} draggable={false} />
            {stage === 'preview' && (
              <TextOverlay
                textLayers={autoTextLayer}
                currentTime={null}
                isDragging={false}
              />
            )}
          </div>
        ) : (
          <div className="auto-video-wrapper">
            <video
              ref={videoRef}
              src={mediaData.mediaUrl}
              playsInline
              muted
              loop
              autoPlay
              onTimeUpdate={() => { if (videoRef.current) setCurrentTime(videoRef.current.currentTime); }}
              draggable={false}
            />
            {stage === 'preview' && (
              <TextOverlay
                textLayers={autoTextLayer}
                currentTime={currentTime}
                isDragging={false}
              />
            )}
          </div>
        )}
      </div>

      {/* Form / Actions */}
      <div className="auto-editor-controls">
        {stage === 'form' ? (
          <>
            <div className="auto-input-group" ref={inputGroupRef}>
              <textarea
                value={wishText}
                onChange={(e) => setWishText(e.target.value)}
                placeholder="By Your Name & Family..."
                rows="3"
                className="auto-textarea"
                autoFocus
              />
              <div className="auto-color-picker">
                <span className="auto-color-label">Text Color</span>
                <div className="auto-color-options">
                  {colorOptions.map((c) => (
                    <button
                      key={c}
                      className={`auto-color-swatch${textColor === c ? ' active' : ''}`}
                      style={{ background: c }}
                      onClick={() => setTextColor(c)}
                      title={c}
                    />
                  ))}
                </div>
              </div>
            </div>
            <button
              className="auto-create-btn"
              onClick={handleCreate}
              disabled={!wishText.trim()}
            >
              <FaMagic /> Create Wish
            </button>
          </>
        ) : (
          <div className="auto-action-buttons">
            <button className="auto-download-btn" onClick={handleDownload} disabled={isProcessing}>
              <FaDownload /> Download
            </button>
            <button
              className="auto-customize-btn"
              onClick={() => navigate(`/wishes/editor/${id}`)}
            >
              <FaEdit /> Customise in Editor
            </button>
            <button
              className="auto-back-btn"
              onClick={() => setStage('form')}
            >
              Edit Text
            </button>
          </div>
        )}
      </div>

      {/* iOS Save Modal */}
      {iosSaveModal.show && (
        <div className="ios-save-overlay" onClick={() => { URL.revokeObjectURL(iosSaveModal.blobUrl); setIosSaveModal({ show: false, blobUrl: '', filename: '' }); }}>
          <div className="ios-save-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Video Ready!</h3>
            <p>Save or share your video</p>
            <button
              className="ios-share-btn"
              onClick={async () => {
                try {
                  const response = await fetch(iosSaveModal.blobUrl);
                  const blob = await response.blob();
                  const file = new File([blob], iosSaveModal.filename, { type: 'video/mp4' });
                  if (navigator.share && navigator.canShare({ files: [file] })) {
                    await navigator.share({ files: [file], title: 'Video' });
                  }
                } catch (err) {
                  if (err.name !== 'AbortError') console.log('Share failed:', err);
                }
              }}
            >
              Share / Save to Photos
            </button>
            <a href={iosSaveModal.blobUrl} download={iosSaveModal.filename} className="ios-download-link">
              Download
            </a>
            <button
              className="ios-close-btn"
              onClick={() => { URL.revokeObjectURL(iosSaveModal.blobUrl); setIosSaveModal({ show: false, blobUrl: '', filename: '' }); }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      <BottomNav activeTab="wishes" logo={logo} />
    </div>
  );
};

export default AutoModeEditor;
